import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../firebase';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function AdminDashboardScreen({ user, navigate, showToast }) {
    const [activeTab, setActiveTab] = useState('stats'); // 'stats', 'users', 'children', 'reports'
    const [users, setUsers] = useState([]);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);

    const [messages, setMessages] = useState([
        { title: '', content: '', imageUrl: '' },
        { title: '', content: '', imageUrl: '' },
        { title: '', content: '', imageUrl: '' }
    ]);
    const [savingMessages, setSavingMessages] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [userRoleFilter, setUserRoleFilter] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    const [childSearchQuery, setChildSearchQuery] = useState('');
    const [childSchoolFilter, setChildSchoolFilter] = useState('');

    // Pagination state
    const [userPage, setUserPage] = useState(1);
    const [childPage, setChildPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch both collections once for stats
                const [usersSnapshot, childrenSnapshot, messagesDoc] = await Promise.all([
                    getDocs(collection(db, 'users')),
                    getDocs(collection(db, 'children')),
                    getDoc(doc(db, 'globalSettings', 'messages'))
                ]);
                
                setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setChildren(childrenSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                
                if (messagesDoc.exists()) {
                    const data = messagesDoc.data().sliderMessages;
                    if (data && data.length > 0) {
                        const paddedData = [...data, { title: '', content: '', imageUrl: '' }, { title: '', content: '', imageUrl: '' }].slice(0, 3);
                        setMessages(paddedData);
                    }
                }
            } catch (error) {
                console.error("Error fetching admin data:", error);
                showToast("Gagal mengambil data", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [showToast]);

    // Pagination Calculations
    const filteredUsers = users.filter(u => {
        const matchesSearch = (u.username || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                              (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                              (u.institutionName || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = userRoleFilter ? u.role === userRoleFilter : true;
        return matchesSearch && matchesRole;
    });

    const filteredChildren = children.filter(c => {
        const matchesSearch = (c.name || '').toLowerCase().includes(childSearchQuery.toLowerCase()) || 
                              (c.nik || '').toLowerCase().includes(childSearchQuery.toLowerCase());
        const matchesSchool = childSchoolFilter ? (c.schoolName || 'Tidak Diketahui').toLowerCase() === childSchoolFilter.toLowerCase() : true;
        return matchesSearch && matchesSchool;
    });
    
    const totalUserPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const totalChildPages = Math.ceil(filteredChildren.length / itemsPerPage);

    const paginatedUsers = filteredUsers.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage);
    const paginatedChildren = filteredChildren.slice((childPage - 1) * itemsPerPage, childPage * itemsPerPage);

    // Stats Calculations
    const examinedChildren = children.filter(c => c.lastAssessment && c.lastAssessment.overallStatus);
    const normalCount = examinedChildren.filter(c => ['Sesuai', 'Normal'].includes(c.lastAssessment.overallStatus)).length;
    const meragukanCount = examinedChildren.filter(c => c.lastAssessment.overallStatus === 'Meragukan').length;
    const penyimpanganCount = examinedChildren.filter(c => ['Ada Penyimpangan', 'Penyimpangan'].includes(c.lastAssessment.overallStatus)).length;
    const rujukanCount = examinedChildren.filter(c => c.lastAssessment.overallStatus === 'Perlu Rujukan Segera').length;

    // Reports Calculations
    const reportsBySchool = children.reduce((acc, child) => {
        let school = (child.schoolName || 'Tidak Diketahui').trim();
        
        // Case-insensitive merge
        const existingKey = Object.keys(acc).find(k => k.toLowerCase() === school.toLowerCase());
        if (existingKey) {
            school = existingKey;
        }

        if (!acc[school]) {
            acc[school] = { total: 0, normal: 0, meragukan: 0, penyimpangan: 0, rujukan: 0 };
        }
        acc[school].total += 1;
        
        if (child.lastAssessment && child.lastAssessment.overallStatus) {
            const status = child.lastAssessment.overallStatus;
            if (['Sesuai', 'Normal'].includes(status)) acc[school].normal += 1;
            else if (status === 'Meragukan') acc[school].meragukan += 1;
            else if (['Ada Penyimpangan', 'Penyimpangan'].includes(status)) acc[school].penyimpangan += 1;
            else if (status === 'Perlu Rujukan Segera') acc[school].rujukan += 1;
        }
        return acc;
    }, {});
    
    const schoolReports = Object.keys(reportsBySchool).map(school => ({
        schoolName: school,
        ...reportsBySchool[school]
    })).sort((a, b) => b.total - a.total);

    const uniqueSchools = Array.from(new Set(children.map(c => c.schoolName ? c.schoolName.trim() : '').filter(Boolean))).sort();


    const handleToggleVerification = async (userId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await updateDoc(doc(db, 'users', userId), { isVerified: newStatus });
            setUsers(users.map(u => u.id === userId ? { ...u, isVerified: newStatus } : u));
            showToast(newStatus ? 'Pengguna diverifikasi' : 'Verifikasi dicabut', 'success');
        } catch (error) {
            console.error("Error updating verification:", error);
            showToast("Gagal memperbarui verifikasi", "error");
        }
    };

    const handleEditClick = (u) => {
        setEditingUser(u.id);
        setEditFormData({
            username: u.username || '',
            role: u.role || 'Unknown',
            institutionName: u.institutionName || ''
        });
    };

    const handleSaveEdit = async (userId) => {
        try {
            await updateDoc(doc(db, 'users', userId), editFormData);
            setUsers(users.map(u => u.id === userId ? { ...u, ...editFormData } : u));
            setEditingUser(null);
            showToast('Data pengguna berhasil diperbarui', 'success');
        } catch (error) {
            console.error("Error updating user:", error);
            showToast("Gagal memperbarui data pengguna", "error");
        }
    };

    const handleResetPassword = async (email) => {
        if (!email) {
            showToast("Email tidak tersedia", "error");
            return;
        }
        if (!window.confirm(`Kirim link reset password ke ${email}?`)) return;
        try {
            await sendPasswordResetEmail(auth, email);
            showToast("Link reset password berhasil dikirim", "success");
        } catch (error) {
            console.error("Error resetting password:", error);
            showToast("Gagal mengirim link reset password", "error");
        }
    };

    const handleExportCSV = () => {
        const headers = ["Nama Anak", "NIK", "Tanggal Lahir", "Institusi", "Status Pemeriksaan Terakhir"];
        const rows = children.map(c => {
            const status = c.lastAssessment && c.lastAssessment.overallStatus ? c.lastAssessment.overallStatus : 'Belum Diperiksa';
            const dob = c.dob && c.dob.seconds ? new Date(c.dob.seconds * 1000).toLocaleDateString('id-ID') : '-';
            return `"${c.name || ''}","${c.nik || ''}","${dob}","${c.schoolName || ''}","${status}"`;
        });
        
        const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "Laporan_SDIDTK.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        const currentDate = new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' });
        
        doc.setFontSize(16);
        doc.text('REKAPITULASI PEMERIKSAAN SDIDTK', 14, 20);
        doc.setFontSize(11);
        doc.text(`Tanggal Export: ${currentDate}`, 14, 28);
        
        let currentY = 38;
        
        Object.keys(reportsBySchool).sort().forEach((school, index) => {
            const schoolNameUpper = school.toUpperCase();
            
            if (currentY > 260) {
                doc.addPage();
                currentY = 20;
            }
            
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(`INSTITUSI: ${schoolNameUpper}`, 14, currentY);
            currentY += 6;
            
            // Get children for this school
            const schoolChildren = children.filter(c => {
                let sName = (c.schoolName || 'Tidak Diketahui').trim();
                const existingKey = Object.keys(reportsBySchool).find(k => k.toLowerCase() === sName.toLowerCase());
                if (existingKey) sName = existingKey;
                return sName === school;
            });
            
            const tableData = schoolChildren.map((c, i) => {
                const status = c.lastAssessment && c.lastAssessment.overallStatus ? c.lastAssessment.overallStatus : 'Belum Diperiksa';
                const dob = c.dob && c.dob.seconds ? new Date(c.dob.seconds * 1000).toLocaleDateString('id-ID') : '-';
                return [
                    i + 1,
                    c.name || '-',
                    c.nik || '-',
                    dob,
                    status
                ];
            });
            
            doc.autoTable({
                startY: currentY,
                head: [['No', 'Nama Anak', 'NIK', 'Tanggal Lahir', 'Status']],
                body: tableData,
                theme: 'grid',
                headStyles: { fillColor: [109, 40, 217] },
                styles: { fontSize: 9 },
                margin: { left: 14, right: 14 },
            });
            
            currentY = doc.lastAutoTable.finalY + 12;
        });
        
        doc.save(`Rekap_SDIDTK_${currentDate.replace(/ /g, '_')}.pdf`);
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Yakin ingin menghapus dokumen pengguna ini?")) return;
        try {
            await deleteDoc(doc(db, 'users', userId));
            setUsers(users.filter(u => u.id !== userId));
            showToast("Pengguna dihapus", "success");
        } catch (error) {
            console.error("Error deleting user:", error);
            showToast("Gagal menghapus pengguna", "error");
        }
    };

    const handleDeleteChild = async (childId) => {
        if (!window.confirm("Yakin ingin menghapus seluruh data anak ini?")) return;
        try {
            await deleteDoc(doc(db, 'children', childId));
            setChildren(children.filter(c => c.id !== childId));
            showToast("Data anak dihapus", "success");
        } catch (error) {
            console.error("Error deleting child:", error);
            showToast("Gagal menghapus anak", "error");
        }
    };

    const handleSaveMessages = async () => {
        setSavingMessages(true);
        try {
            await setDoc(doc(db, 'globalSettings', 'messages'), { sliderMessages: messages });
            showToast("Pesan edukasi berhasil disimpan", "success");
        } catch (error) {
            console.error("Error saving messages:", error);
            showToast("Gagal menyimpan pesan edukasi", "error");
        } finally {
            setSavingMessages(false);
        }
    };

    return (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 transition-all duration-300">
            <h2 className="text-2xl font-extrabold text-gray-800 mb-6 flex items-center space-x-2">
                <span className="text-3xl">👑</span>
                <span>Dashboard Admin</span>
            </h2>
            <div className="flex flex-wrap gap-3 mb-6">
                <button 
                    onClick={() => setActiveTab('stats')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'stats' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    📊 Statistik
                </button>
                <button 
                    onClick={() => setActiveTab('users')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'users' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    👨‍👩‍👧‍👦 Kelola Pengguna
                </button>
                <button 
                    onClick={() => setActiveTab('children')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'children' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    👶 Kelola Data Anak
                </button>
                <button 
                    onClick={() => setActiveTab('reports')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'reports' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    📑 Laporan Institusi
                </button>
                <button 
                    onClick={() => setActiveTab('messages')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'messages' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    📢 Pesan Edukasi
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center p-10"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div></div>
            ) : (
                <div className="space-y-4">
                    {activeTab === 'stats' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-blue-600 text-sm font-bold uppercase tracking-wider mb-1">Total Pengguna</p>
                                <p className="text-3xl font-extrabold text-blue-900">{users.length}</p>
                            </div>
                            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-purple-600 text-sm font-bold uppercase tracking-wider mb-1">Total Anak</p>
                                <p className="text-3xl font-extrabold text-purple-900">{children.length}</p>
                            </div>
                            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-indigo-600 text-sm font-bold uppercase tracking-wider mb-1">Anak Diperiksa</p>
                                <p className="text-3xl font-extrabold text-indigo-900">{examinedChildren.length}</p>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-emerald-600 text-sm font-bold uppercase tracking-wider mb-1">Status Normal</p>
                                <p className="text-3xl font-extrabold text-emerald-900">{normalCount}</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-amber-600 text-sm font-bold uppercase tracking-wider mb-1">Meragukan</p>
                                <p className="text-3xl font-extrabold text-amber-900">{meragukanCount}</p>
                            </div>
                            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-rose-600 text-sm font-bold uppercase tracking-wider mb-1">Penyimpangan & Rujukan</p>
                                <p className="text-3xl font-extrabold text-rose-900">{penyimpanganCount + rujukanCount}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="space-y-4">
                            <div className="mb-4 flex flex-col sm:flex-row gap-3">
                                <input 
                                    type="text" 
                                    placeholder="Cari nama, email, atau institusi..." 
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setUserPage(1);
                                    }}
                                    className="w-full sm:w-2/3 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <select 
                                    value={userRoleFilter} 
                                    onChange={(e) => {
                                        setUserRoleFilter(e.target.value);
                                        setUserPage(1);
                                    }}
                                    className="w-full sm:w-1/3 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                    <option value="">Semua Peran</option>
                                    <option value="Orang Tua">Orang Tua</option>
                                    <option value="Guru PAUD">Guru PAUD</option>
                                    <option value="Tenaga Kesehatan">Tenaga Kesehatan</option>
                                    <option value="Admin">Admin</option>
                                </select>
                            </div>
                            {paginatedUsers.map(u => (
                        <div key={u.id} className="p-4 border rounded-2xl flex flex-col sm:flex-row sm:items-start justify-between gap-4 bg-gray-50/50 hover:bg-gray-50 transition">
                            {editingUser === u.id ? (
                                <div className="flex-1 w-full space-y-3">
                                    <input type="text" value={editFormData.username} onChange={e => setEditFormData({...editFormData, username: e.target.value})} className="w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-1 focus:ring-purple-500" placeholder="Nama" />
                                    <input type="text" value={editFormData.institutionName} onChange={e => setEditFormData({...editFormData, institutionName: e.target.value})} className="w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-1 focus:ring-purple-500" placeholder="Institusi" />
                                    <select value={editFormData.role} onChange={e => setEditFormData({...editFormData, role: e.target.value})} className="w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-1 focus:ring-purple-500">
                                        <option value="Orang Tua">Orang Tua</option>
                                        <option value="Guru PAUD">Guru PAUD</option>
                                        <option value="Tenaga Kesehatan">Tenaga Kesehatan</option>
                                        <option value="Admin">Admin</option>
                                    </select>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleSaveEdit(u.id)} className="px-4 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-lg hover:bg-emerald-600 transition">Simpan</button>
                                        <button onClick={() => setEditingUser(null)} className="px-4 py-1.5 bg-gray-300 text-gray-700 text-xs font-bold rounded-lg hover:bg-gray-400 transition">Batal</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                <div className="flex-1">
                                    <h3 className="font-bold text-gray-800">{u.username || u.email || 'Tanpa Nama'}</h3>
                                    <p className="text-xs text-gray-500">{u.email || '-'}</p>
                                    {u.lastLogin && (
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                            Aktivitas Terakhir: {u.lastLogin?.seconds ? new Date(u.lastLogin.seconds * 1000).toLocaleString('id-ID') : '-'}
                                        </p>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <span className="px-2 py-1 bg-gray-200 text-gray-700 text-[10px] rounded uppercase font-bold">{u.role || 'Unknown'}</span>
                                        {(u.role === 'Guru PAUD' || u.role === 'Tenaga Kesehatan') && (
                                            <span className={`px-2 py-1 text-[10px] rounded uppercase font-bold ${u.isVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {u.isVerified ? 'Terverifikasi' : 'Belum Diverifikasi'}
                                            </span>
                                        )}
                                    </div>
                                    {u.institutionName && <p className="text-[11px] mt-1.5 font-medium text-gray-600">🏢 {u.institutionName}</p>}
                                </div>
                                <div className="flex flex-wrap gap-2 sm:justify-end">
                                    {(u.role === 'Guru PAUD' || u.role === 'Tenaga Kesehatan') && (
                                        <button 
                                            onClick={() => handleToggleVerification(u.id, u.isVerified)}
                                            className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${u.isVerified ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                                        >
                                            {u.isVerified ? 'Cabut Verifikasi' : 'Verifikasi'}
                                        </button>
                                    )}
                                    <button onClick={() => handleEditClick(u)} className="px-3 py-1.5 bg-blue-100 text-blue-600 text-[11px] font-bold rounded-lg hover:bg-blue-200 transition-colors">
                                        ✏️ Edit
                                    </button>
                                    {u.email && (
                                        <button onClick={() => handleResetPassword(u.email)} className="px-3 py-1.5 bg-purple-100 text-purple-600 text-[11px] font-bold rounded-lg hover:bg-purple-200 transition-colors">
                                            🔑 Reset Password
                                        </button>
                                    )}
                                    <button onClick={() => handleDeleteUser(u.id)} className="px-3 py-1.5 bg-red-100 text-red-600 text-[11px] font-bold rounded-lg hover:bg-red-200 transition-colors">
                                        🗑️ Hapus
                                    </button>
                                </div>
                                </>
                            )}
                        </div>
                    ))}
                            {/* Pagination Controls for Users */}
                            {totalUserPages > 1 && (
                                <div className="flex items-center justify-between mt-6 bg-gray-50 p-3 rounded-xl border border-gray-200">
                                    <button 
                                        disabled={userPage === 1} 
                                        onClick={() => setUserPage(userPage - 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Sebelumnya
                                    </button>
                                    <span className="text-sm font-bold text-gray-600">Halaman {userPage} dari {totalUserPages}</span>
                                    <button 
                                        disabled={userPage === totalUserPages} 
                                        onClick={() => setUserPage(userPage + 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Selanjutnya
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'children' && (
                        <div className="space-y-4">
                            <div className="mb-4 flex flex-col sm:flex-row gap-3">
                                <input 
                                    type="text" 
                                    placeholder="Cari nama atau NIK anak..." 
                                    value={childSearchQuery}
                                    onChange={(e) => {
                                        setChildSearchQuery(e.target.value);
                                        setChildPage(1);
                                    }}
                                    className="w-full sm:w-2/3 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <select 
                                    value={childSchoolFilter} 
                                    onChange={(e) => {
                                        setChildSchoolFilter(e.target.value);
                                        setChildPage(1);
                                    }}
                                    className="w-full sm:w-1/3 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                    <option value="">Semua Institusi</option>
                                    {uniqueSchools.map((school, i) => (
                                        <option key={i} value={school}>{school}</option>
                                    ))}
                                    <option value="Tidak Diketahui">Tidak Diketahui</option>
                                </select>
                            </div>
                            {paginatedChildren.map(c => (
                        <div key={c.id} className="p-4 border rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 hover:bg-gray-50 transition">
                            <div>
                                <h3 className="font-bold text-gray-800">{c.name || 'Tanpa Nama'}</h3>
                                <p className="text-xs text-gray-500 font-mono mt-0.5">NIK: {c.nik || 'Tidak ada'}</p>
                                {c.schoolName && <p className="text-[11px] mt-1 text-gray-600">🏫 {c.schoolName}</p>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => navigate('viewChild', c)} className="px-3 py-1.5 bg-blue-100 text-blue-700 text-[11px] font-bold rounded-lg hover:bg-blue-200 transition-colors">
                                    👁️ Lihat
                                </button>
                                <button onClick={() => navigate('editChild', c)} className="px-3 py-1.5 bg-purple-100 text-purple-700 text-[11px] font-bold rounded-lg hover:bg-purple-200 transition-colors">
                                    ✏️ Edit
                                </button>
                                <button onClick={() => handleDeleteChild(c.id)} className="px-3 py-1.5 bg-red-100 text-red-600 text-[11px] font-bold rounded-lg hover:bg-red-200 transition-colors">
                                    🗑️ Hapus
                                </button>
                            </div>
                        </div>
                    ))}
                            {/* Pagination Controls for Children */}
                            {totalChildPages > 1 && (
                                <div className="flex items-center justify-between mt-6 bg-gray-50 p-3 rounded-xl border border-gray-200">
                                    <button 
                                        disabled={childPage === 1} 
                                        onClick={() => setChildPage(childPage - 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Sebelumnya
                                    </button>
                                    <span className="text-sm font-bold text-gray-600">Halaman {childPage} dari {totalChildPages}</span>
                                    <button 
                                        disabled={childPage === totalChildPages} 
                                        onClick={() => setChildPage(childPage + 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Selanjutnya
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'reports' && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap justify-end gap-3 mb-4">
                                <button onClick={handleExportCSV} className="px-4 py-2 bg-emerald-500 text-white font-bold rounded-xl shadow-md hover:bg-emerald-600 transition flex items-center gap-2">
                                    <span>⬇️</span> Export CSV
                                </button>
                                <button onClick={handleExportPDF} className="px-4 py-2 bg-rose-500 text-white font-bold rounded-xl shadow-md hover:bg-rose-600 transition flex items-center gap-2">
                                    <span>📄</span> Export PDF
                                </button>
                            </div>
                            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                                <table className="w-full text-sm text-left text-gray-500 whitespace-nowrap">
                                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3">Institusi / Sekolah</th>
                                            <th className="px-4 py-3 text-center">Total Anak</th>
                                            <th className="px-4 py-3 text-center text-emerald-600">Normal</th>
                                            <th className="px-4 py-3 text-center text-amber-600">Meragukan</th>
                                            <th className="px-4 py-3 text-center text-rose-600">Penyimpangan/Rujukan</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schoolReports.length > 0 ? schoolReports.map((report, idx) => (
                                            <tr key={idx} className="bg-white border-b hover:bg-gray-50">
                                                <td className="px-4 py-3 font-medium text-gray-900">{report.schoolName}</td>
                                                <td className="px-4 py-3 text-center font-bold">{report.total}</td>
                                                <td className="px-4 py-3 text-center">{report.normal}</td>
                                                <td className="px-4 py-3 text-center">{report.meragukan}</td>
                                                <td className="px-4 py-3 text-center">{report.penyimpangan + report.rujukan}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">Belum ada data laporan institusi.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'messages' && (
                        <div className="space-y-6">
                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <h3 className="font-bold text-blue-800 mb-1">Panduan Pengisian Pesan Edukasi (Slider)</h3>
                                <ul className="text-sm text-blue-700 space-y-1 list-disc pl-5">
                                    <li>Pesan ini akan ditampilkan di halaman depan (Dashboard) pengguna.</li>
                                    <li>Anda dapat mengisi hingga 3 pesan berbeda.</li>
                                    <li>Kolom <strong>URL Gambar</strong> opsional. Jika diisi, pastikan gambar memiliki orientasi lanskap (contoh rasio 16:9 atau resolusi 800x450 piksel) agar tampilan tidak terpotong.</li>
                                </ul>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {messages.map((msg, index) => (
                                    <div key={index} className="bg-gray-50 p-5 rounded-2xl border border-gray-200">
                                        <h4 className="font-bold text-gray-700 mb-4">Pesan Edukasi {index + 1}</h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Judul Singkat</label>
                                                <input 
                                                    type="text" 
                                                    value={msg.title}
                                                    onChange={e => {
                                                        const newMsgs = [...messages];
                                                        newMsgs[index].title = e.target.value;
                                                        setMessages(newMsgs);
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                                    placeholder="Contoh: Pentingnya Gizi Seimbang"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Konten / Isi Pesan</label>
                                                <textarea 
                                                    value={msg.content}
                                                    onChange={e => {
                                                        const newMsgs = [...messages];
                                                        newMsgs[index].content = e.target.value;
                                                        setMessages(newMsgs);
                                                    }}
                                                    rows="3"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                                    placeholder="Tuliskan pesan parenting di sini..."
                                                ></textarea>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">URL Gambar (Opsional)</label>
                                                <input 
                                                    type="text" 
                                                    value={msg.imageUrl}
                                                    onChange={e => {
                                                        const newMsgs = [...messages];
                                                        newMsgs[index].imageUrl = e.target.value;
                                                        setMessages(newMsgs);
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                                    placeholder="https://example.com/image.jpg"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="flex justify-end mt-4">
                                <button 
                                    onClick={handleSaveMessages}
                                    disabled={savingMessages}
                                    className="px-6 py-3 bg-purple-600 text-white font-bold rounded-xl shadow-md hover:bg-purple-700 transition disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {savingMessages ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>Menyimpan...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>💾</span>
                                            <span>Simpan Pesan Edukasi</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && filteredUsers.length === 0 && !loading && (
                        <p className="text-center text-gray-500 py-10">Tidak ada data pengguna.</p>
                    )}
                    {activeTab === 'children' && children.length === 0 && !loading && (
                        <p className="text-center text-gray-500 py-10">Tidak ada data anak.</p>
                    )}
                </div>
            )}
        </div>
    );
}
